/**
 * The last thing an author typed, kept where the network cannot lose it.
 *
 * Autosave covers the ordinary case, but it is debounced: the seconds between
 * the last keystroke and the request landing are unprotected, and that is
 * exactly the window a crashed tab, a closed laptop or a reload falls into.
 * Writing to `localStorage` on every change costs nothing, needs no round trip,
 * and survives all three.
 *
 * This is a safety net, never a source of truth. The article on the server is
 * the record; a stored draft is only ever *offered* back to the person who typed
 * it, and is thrown away the moment a save succeeds. Restoring silently would be
 * worse than losing it - two tabs, or a colleague's edit, and the older text
 * would quietly overwrite the newer without anybody choosing that.
 */

/** Namespaced per article, so two open articles cannot overwrite each other. */
const keyFor = (articleId: string): string => `kiji.draft.${articleId}`;

export interface StoredDraft {
  /** Only the fields an author types. Status and references are not work. */
  title: string;
  bodyMd: string;
  keywords: string[];
  /** The article version this was typed against, to spot a stale draft. */
  version: number;
  /** When it was stored, so the offer can say how old it is. */
  savedAt: string;
}

export function saveDraft(articleId: string, draft: Omit<StoredDraft, 'savedAt'>): void {
  try {
    localStorage.setItem(
      keyFor(articleId),
      JSON.stringify({ ...draft, savedAt: new Date().toISOString() } satisfies StoredDraft),
    );
  } catch {
    /*
     * Private browsing, a full quota, or storage disabled by policy. The draft
     * is a safety net; failing to lay it out must never break the editor the
     * author is currently typing into.
     */
  }
}

export function clearDraft(articleId: string): void {
  try {
    localStorage.removeItem(keyFor(articleId));
  } catch {
    // Same reasoning as above.
  }
}

export function readDraft(articleId: string): StoredDraft | null {
  try {
    const raw = localStorage.getItem(keyFor(articleId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isDraft(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Stored data is input, even when this application stored it.
 *
 * It survives across releases, so a draft written by an older build - or edited
 * by hand in devtools - has to be checked rather than trusted.
 */
function isDraft(value: unknown): value is StoredDraft {
  if (typeof value !== 'object' || value === null) return false;
  const draft = value as Record<string, unknown>;
  return (
    typeof draft.title === 'string' &&
    typeof draft.bodyMd === 'string' &&
    Array.isArray(draft.keywords) &&
    draft.keywords.every((entry) => typeof entry === 'string') &&
    typeof draft.version === 'number' &&
    typeof draft.savedAt === 'string'
  );
}

/**
 * Whether a stored draft is worth offering back.
 *
 * Three ways it is not, and each has cost somebody an unpleasant surprise in
 * some other application:
 *
 *   - It matches what the server already has. The save landed; the draft is just
 *     a leftover, and offering it would imply work was lost when none was.
 *   - It was typed against an older version of the article. Somebody else has
 *     saved since - restoring would silently discard their edit, so the draft is
 *     stale and the person keeps what is on the server.
 *   - It is empty. Nothing to restore.
 */
export function isRecoverable(
  draft: StoredDraft | null,
  server: { title: string; bodyMd: string; keywords: string[]; version: number },
): boolean {
  if (!draft) return false;
  if (draft.version !== server.version) return false;
  if (draft.bodyMd.trim() === '' && draft.title.trim() === '') return false;
  return (
    draft.bodyMd !== server.bodyMd ||
    draft.title !== server.title ||
    draft.keywords.join(' ') !== server.keywords.join(' ')
  );
}
